'use client'

import { ArrowLeft24, X24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Divider, Navigation, Spacer, Text } from '@vinted/web-ui'
import { useCallback, useEffect, useState } from 'react'

import {
  DropOffPointSource,
  useDropOffPointMapContext,
} from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { ShowAtBreakpoint, useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useEnvs } from '@marketplace-web/environment/environment-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  CoordinatesModel,
  getCountryBounds,
  transformCountryBounds,
} from '@marketplace-web/shipping/checkout-shipping-data'
import { useLoadGoogleApi } from '@marketplace-web/shipping/google-maps-feature'
import {
  geocodeAddress,
  GoogleAutocompleteType,
  GooglePlacesAutocomplete,
} from '@marketplace-web/shipping/google-places-feature'
import {
  getDefaultShippingAddress,
  getFormattedAddress,
  transformDefaultShippingAddress,
} from '@marketplace-web/address/address-data'

import { clickEvent } from '@marketplace-web/conversation-shipping/conversation-shipping-data'

import { DropOffPointDetails } from './DropOffPointMap/components'
import DropOffPointMap from './DropOffPointMap/DropOffPointMap'

const AUTOCOMPLETE_PHONES_MAX_HEIGHT = 200

type Props = {
  isOpen: boolean
  onClose: () => void
}

const DropOffPointModal = ({ isOpen, onClose }: Props) => {
  const translate = useTranslate('conversation.drop_off_point_modal')
  const breakpoints = useBreakpoint()
  const { track } = useTracking()
  const { conversation } = useConversationContext()
  const sessionCountryId = useSession().user?.country_id
  const countryIsoCode = useSession().user?.country_iso_code

  const {
    fetchNearbyDropOffPoints,
    selectedDropOffPoint,
    dropOffPointCarrier,
    nearbyDropOffPoints,
    uiState,
    selectDropOffPoint,
  } = useDropOffPointMapContext()

  const googleApiKey = useEnvs('GOOGLE_MAPS_API_KEY') || ''
  const transactionId = conversation?.transaction?.id
  const shipmentStatus = conversation?.transaction?.shipment?.status

  const [autocompleteValue, setAutocompleteValue] = useState('')
  const [addressCoordinates, setAddressCoordinates] = useState<CoordinatesModel | null>(null)
  const [isAdvancedDetailsOpen, setIsAdvancedDetailsOpen] = useState(false)

  const isGoogleApiLoaded = useLoadGoogleApi()

  const { fetch: fetchDefaultShippingAddress, transformedData: defaultShippingAddress } = useFetch(
    getDefaultShippingAddress,
    transformDefaultShippingAddress,
  )
  const { fetch: fetchCountryBounds, transformedData: countryBounds } = useFetch(
    getCountryBounds,
    transformCountryBounds,
  )

  const formattedAddress = getFormattedAddress(defaultShippingAddress)

  useEffect(() => {
    if (!isOpen) return

    fetchDefaultShippingAddress()
  }, [fetchDefaultShippingAddress, isOpen])

  const geocodeUserAddress = useCallback(async () => {
    if (!isOpen || !formattedAddress) return

    const geocodedAddress = await geocodeAddress(formattedAddress)

    if (!geocodedAddress?.formatted_address || !geocodedAddress?.geometry) return

    const geocodedAddressCoordinates = {
      latitude: geocodedAddress.geometry.location.lat(),
      longitude: geocodedAddress.geometry.location.lng(),
    }

    setAutocompleteValue(geocodedAddress.formatted_address)
    setAddressCoordinates(geocodedAddressCoordinates)
  }, [formattedAddress, isOpen])

  useEffect(() => {
    if (!isGoogleApiLoaded) return

    geocodeUserAddress()
  }, [isGoogleApiLoaded, geocodeUserAddress])

  useEffect(() => {
    if (!isOpen) return

    const countryId = defaultShippingAddress?.countryId || sessionCountryId
    if (!countryId) return

    fetchCountryBounds(Number(countryId))
  }, [defaultShippingAddress, fetchCountryBounds, isOpen, sessionCountryId])

  const trackSearchClick = () => {
    const event = clickEvent({
      screen: 'seller_drop_off_map',
      target: 'address_search_bar',
      targetDetails: JSON.stringify({
        transaction_id: transactionId,
        shipment_status: shipmentStatus,
      }),
    })

    track(event)
  }

  const handleAutocompleteSetValue = (_: string | null, suggestion?: string) => {
    if (!suggestion) return

    setAutocompleteValue(suggestion)
  }

  const handleSetPlaceDetails = ({ geometry }: google.maps.places.PlaceResult) => {
    if (!geometry?.location) return

    const coordinates = {
      latitude: geometry.location.lat(),
      longitude: geometry.location.lng(),
    }

    fetchNearbyDropOffPoints({
      coordinates,
      dropOffPointSource: DropOffPointSource.SearchBarAddress,
    })

    setAddressCoordinates(addressCoordinates)
  }

  const handleModalClose = () => {
    setIsAdvancedDetailsOpen(false)

    onClose()
  }

  const toggleSelectedPointDetailsState = () => {
    setIsAdvancedDetailsOpen(prevState => !prevState)
  }

  const removeSelectedPoint = () => {
    if (breakpoints.phones) return

    selectDropOffPoint(null)
  }

  const renderDropOffPointDetails = () => {
    if (!countryBounds || !isGoogleApiLoaded || !selectedDropOffPoint) return null

    return (
      <DropOffPointDetails
        selectedDropOffPoint={selectedDropOffPoint}
        dropOffPointCarrier={dropOffPointCarrier}
        isAdvancedDetailsOpen={isAdvancedDetailsOpen}
        toggleSelectedPointDetailsState={toggleSelectedPointDetailsState}
      />
    )
  }

  const renderAutocomplete = (name: string, placeholder: string, maxHeight?: number) => {
    if (!isGoogleApiLoaded) return null

    return (
      <GooglePlacesAutocomplete
        type={GoogleAutocompleteType.InputBar}
        name={name}
        placeholder={placeholder}
        initialValue={autocompleteValue}
        country={defaultShippingAddress?.countryIsoCode || countryIsoCode}
        isLoading={uiState === UiState.Pending}
        onValueChange={handleAutocompleteSetValue}
        suggestionsListMaxHeight={maxHeight}
        screen="seller_drop_off_map"
        onPlaceDetailsChange={handleSetPlaceDetails}
      />
    )
  }

  const renderMap = () => {
    if (!countryBounds || !isGoogleApiLoaded) return null

    return (
      <DropOffPointMap
        googleApiKey={googleApiKey}
        onMapClick={removeSelectedPoint}
        addressCoordinates={addressCoordinates}
        countryBounds={countryBounds}
      />
    )
  }

  const renderNavigation = () => (
    <Navigation
      left={
        isAdvancedDetailsOpen && (
          <ShowAtBreakpoint breakpoint="phones">
            <Button
              iconName={ArrowLeft24}
              styling="flat"
              onClick={toggleSelectedPointDetailsState}
              inline
            />
          </ShowAtBreakpoint>
        )
      }
      body={<Text as="h2" text={translate('navigation.body')} type="title" />}
      right={
        <Button
          iconName={X24}
          styling="flat"
          onClick={handleModalClose}
          inline
          testId="drop-off-point-modal-close-button"
        />
      }
    />
  )

  const renderEmptyStateNote = () => (
    <Cell>
      <Text
        as="h2"
        text={translate('tablets_up.empty_state.body')}
        type="title"
        alignment="center"
        width="parent"
      />
    </Cell>
  )

  const renderPhonesEmptyState = () => (
    <>
      <Divider />
      <Spacer size="regular" />
      <div className="drop-off-point-map-modal--phones__container">
        <div className="u-text-left">
          {renderAutocomplete(
            'drop-off-places-autocomplete-phones',
            translate('autocomplete.placeholder'),
            AUTOCOMPLETE_PHONES_MAX_HEIGHT,
          )}
        </div>
        <Spacer size="regular" />
        {renderMap()}
        <div className="u-flexbox">{renderEmptyStateNote()}</div>
      </div>
    </>
  )

  const renderPhones = () => {
    if (!nearbyDropOffPoints?.length) {
      return renderPhonesEmptyState()
    }

    if (isAdvancedDetailsOpen) return renderDropOffPointDetails()

    return (
      <>
        <div
          role="none"
          className="u-ui-padding-regular"
          onClick={trackSearchClick}
          data-testid="search-bar-phones"
        >
          {renderAutocomplete(
            'drop-off-places-autocomplete-phones',
            translate('autocomplete.placeholder'),
          )}
        </div>
        <Divider />
        <div className="drop-off-point-map-modal__content">
          <div className="pickup-point-modal__map-container">
            {renderMap()}
            {renderDropOffPointDetails()}
          </div>
        </div>
      </>
    )
  }

  const renderTabletsUpEmptyState = () => (
    <>
      {isGoogleApiLoaded && (
        <div
          role="none"
          className="u-ui-padding-regular"
          onClick={trackSearchClick}
          data-testid="search-bar-tablets-up"
        >
          {renderAutocomplete(
            'drop-off-places-autocomplete-tablets-up',
            translate('autocomplete.placeholder'),
          )}
        </div>
      )}
      {renderMap()}
      {renderEmptyStateNote()}
    </>
  )

  const renderTabletsUp = () => {
    if (!nearbyDropOffPoints?.length && !defaultShippingAddress) {
      return renderTabletsUpEmptyState()
    }

    return (
      <>
        {isGoogleApiLoaded && (
          <div
            role="none"
            className="u-ui-padding-regular"
            onClick={trackSearchClick}
            data-testid="search-bar-tablets-up"
          >
            {renderAutocomplete(
              'drop-off-places-autocomplete-tablets-up',
              translate('autocomplete.placeholder'),
            )}
          </div>
        )}
        {renderMap()}
        {renderDropOffPointDetails()}
      </>
    )
  }

  const contentDimensions = breakpoints.wide
    ? { width: '1240px', height: 'calc(100% - 80px)' }
    : { width: '100%', height: '100%' }

  return (
    <Dialog isModal={false} show={isOpen} contentDimensions={contentDimensions}>
      <div className="drop-off-point-map-modal" data-testid="drop-off-point-map-modal">
        {renderNavigation()}
        <ShowAtBreakpoint breakpoint="tabletsUp">
          <Divider />
          {renderTabletsUp()}
        </ShowAtBreakpoint>
        <ShowAtBreakpoint breakpoint="phones">{renderPhones()}</ShowAtBreakpoint>
      </div>
    </Dialog>
  )
}

export default DropOffPointModal
